/**
 * Phase 135.1 — Deck-vs-worksheet mirroring audit.
 *
 * A compact, expandable pill that lives between the deck preview and
 * the worksheet preview in the lesson workspace. When opened, it
 * fetches the slide spec via `/api/activities/:id/slide-spec` and
 * cross-checks each numbered worksheet prompt against the deck. Any
 * prompt that doesn't show up (by number AND by distinctive
 * substring) is surfaced as a "missing from the deck" warning so the
 * teacher can decide whether to regenerate or hand-edit in Canva.
 *
 * The audit uses the SAME transferable-skill self-assessment filter
 * as the backend (`slide_decks._is_skill_self_assessment_sheet`) so
 * print-only sheets don't show up as false misses.
 */
import { useState } from "react";
import { AlertTriangle, CheckCircle2, FileSearch, Loader2 } from "lucide-react";
import { getSlideSpec } from "../lib/api";

// Mirrors backend `_SKILL_SELF_ASSESSMENT_TITLE_PATTERNS` in
// `slide_decks.py`. Both halves of the system stay aligned so a sheet
// that's excluded from the deck never reads as "missing" here.
const SKILL_SELF_ASSESSMENT_PATTERNS = [
  "transferable skill self",
  "transferable-skill self",
  "skill self-assessment",
  "skill self assessment",
  "skill self-rating",
  "skill self rating",
  "skill self-reflection",
  "skill self reflection",
  "self-assessment: skill",
  "self assessment: skill",
  "i-can skill",
  "i can skill",
  "transferable skill reflection",
  "transferable-skill reflection",
  "transferable skill checklist",
  "transferable-skill checklist",
];

const isSkillSelfAssessment = (sheet) => {
  const title = String(sheet?.title || "").toLowerCase();
  if (!title) return false;
  if (SKILL_SELF_ASSESSMENT_PATTERNS.some((p) => title.includes(p))) return true;
  const stype = String(sheet?.sheet_type || "").toLowerCase();
  if (
    stype === "checklist" &&
    (title.includes("transferable skill") ||
      title.includes("transferable-skill") ||
      title.includes("i can") ||
      title.includes("i-can"))
  ) {
    return true;
  }
  return false;
};

// Walk flat-or-nested student_sheets shape, drop the skill self-
// assessment, and return a flat list of sheets to audit.
const collectAuditableSheets = (raw) => {
  if (!Array.isArray(raw)) return [];
  const flat = [];
  raw.forEach((s) => {
    if (!s || typeof s !== "object") return;
    if (Array.isArray(s.sheets)) {
      s.sheets.forEach((nested) => {
        if (nested && typeof nested === "object") flat.push(nested);
      });
    } else {
      flat.push(s);
    }
  });
  return flat.filter((s) => !isSkillSelfAssessment(s));
};

// Extract numbered prompts from a markdown body. Matches "1. ", "2. ",
// etc. at the start of a line. Stops at the next numbered prompt, a
// blank line, or end of body.
const NUMBERED_PROMPT_RE = /^(\d+)\.\s+(.+?)(?=\n\s*\d+\.\s+|\n\s*\n|$)/gms;

const extractPrompts = (sheet) => {
  const md = String(sheet?.content_markdown || "");
  if (!md.trim()) return [];
  const out = [];
  let m;
  // Reset lastIndex defensively — global regexes are stateful.
  NUMBERED_PROMPT_RE.lastIndex = 0;
  while ((m = NUMBERED_PROMPT_RE.exec(md)) !== null) {
    const num = m[1];
    const firstLine = m[2].split("\n")[0].trim();
    const preview = firstLine.length > 90 ? `${firstLine.slice(0, 87)}…` : firstLine;
    if (preview) {
      out.push({ sheetTitle: sheet.title || "Sheet", num, text: preview });
    }
  }
  return out;
};

// Concat every visible text on a slide so we can substring-check.
const slideHaystack = (s) => {
  const parts = [s?.title, s?.body, ...(Array.isArray(s?.bullets) ? s.bullets : []), s?.callout];
  return parts.filter(Boolean).join(" \n ").toLowerCase();
};

// A prompt is "mirrored" on the deck when both its leading number
// ("3.") AND its first 3-4 distinctive (≥4 letter) words appear on
// the same slide. Loose enough to allow minor rewording, strict
// enough to fail when a question is genuinely missing.
const isPromptMirrored = (prompt, slideTexts) => {
  const needleNum = `${prompt.num}.`;
  const distinctive = prompt.text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length >= 4)
    .slice(0, 3);
  return slideTexts.some((t) => {
    if (!t.includes(needleNum)) return false;
    if (distinctive.length === 0) return true;
    return distinctive.every((w) => t.includes(w));
  });
};

export const DeckMirroringAudit = ({ activity }) => {
  const [slides, setSlides] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const sheets = collectAuditableSheets(activity?.student_sheets);
  const prompts = sheets.flatMap(extractPrompts);

  if (!activity?.id || prompts.length === 0) return null;

  const fetchOnce = () => {
    if (slides || loading) return;
    setLoading(true);
    setErr(null);
    getSlideSpec(activity.id)
      .then((spec) => setSlides(Array.isArray(spec?.slides) ? spec.slides : []))
      .catch((e) => {
        const detail =
          e?.response?.data?.detail || e?.message || "Couldn't load deck spec.";
        setErr(String(detail));
      })
      .finally(() => setLoading(false));
  };

  const onToggle = () => {
    if (!open) fetchOnce();
    setOpen((v) => !v);
  };

  const slideTexts = slides ? slides.map(slideHaystack) : [];
  const missing = slides
    ? prompts.filter((p) => !isPromptMirrored(p, slideTexts))
    : [];
  const allMatched = !!slides && missing.length === 0;

  // Phase 135.1 — Reading pagination quality. The backend chunks
  // reading bodies at ≤ 520 chars so the renderer can hold a fixed
  // 18 pt body size with no shrink-to-fit. Surface the same number
  // here so the teacher can confirm the invariant on real lessons.
  const READING_CHUNK_LIMIT = 520;
  const readingSlides = slides
    ? slides.filter((s) => s?.type === "reading")
    : [];
  const longestReadingChars = readingSlides.reduce(
    (acc, s) => Math.max(acc, String(s?.body || "").length),
    0,
  );
  const paginationOk = longestReadingChars <= READING_CHUNK_LIMIT;

  const summary = (() => {
    if (!open) return `${prompts.length} worksheet items`;
    if (loading) return "Checking…";
    if (err) return "Couldn't check";
    if (allMatched) return `✓ All ${prompts.length} items on deck`;
    return `⚠ ${missing.length} of ${prompts.length} missing`;
  })();

  return (
    <section
      data-testid="deck-mirroring-audit"
      className="rounded-xl border border-[#4a6984]/15 bg-white overflow-hidden"
    >
      <button
        type="button"
        data-testid="deck-mirroring-audit-toggle"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#f4f5f7] transition text-left"
      >
        <span className="inline-flex items-center gap-2 text-[#1c2d3d] font-extrabold text-sm">
          <FileSearch className="h-4 w-4" />
          Verify deck mirrors worksheets
        </span>
        <span
          data-testid="deck-mirroring-audit-summary"
          className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/80 inline-flex items-center gap-1.5"
        >
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {summary}
        </span>
      </button>
      {open && (
        <div
          data-testid="deck-mirroring-audit-body"
          className="px-4 py-3 border-t border-[#4a6984]/10 bg-[#f4f5f7]"
        >
          {loading && (
            <p className="inline-flex items-center gap-2 text-sm text-[#4a6984]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cross-checking deck against {prompts.length} worksheet items…
            </p>
          )}
          {!loading && err && (
            <p className="text-sm text-[#a23a3a]" data-testid="deck-mirroring-audit-error">
              {err}
            </p>
          )}
          {!loading && !err && allMatched && (
            <p
              data-testid="deck-mirroring-audit-all-good"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#1a7a47]"
            >
              <CheckCircle2 className="h-4 w-4" />
              Every worksheet item is on the deck.
            </p>
          )}
          {!loading && !err && !allMatched && missing.length > 0 && (
            <>
              <p className="text-sm font-bold text-[#a23a3a] mb-2 inline-flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                These worksheet items aren&apos;t on the deck yet:
              </p>
              <ul
                data-testid="deck-mirroring-audit-missing-list"
                className="space-y-1.5"
              >
                {missing.map((p, i) => (
                  <li
                    key={`${p.sheetTitle}-${p.num}-${i}`}
                    data-testid={`deck-mirroring-audit-missing-${i}`}
                    className="text-sm text-[#1c2d3d] pl-3 border-l-2 border-[#a23a3a]/40"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#4a6984]/70 block">
                      {p.sheetTitle} · Q{p.num}
                    </span>
                    {p.text}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-[#4a6984]/80 leading-snug">
                Regenerate the deck, or open the .pptx in Canva to add these
                items by hand. The skill self-assessment sheet is print-only
                and is excluded from this check.
              </p>
            </>
          )}
          {!loading && !err && readingSlides.length > 0 && (
            <p
              data-testid="deck-mirroring-audit-reading-pagination"
              className={`mt-3 pt-3 border-t border-[#4a6984]/10 text-xs leading-snug ${
                paginationOk ? "text-[#4a6984]/85" : "text-[#a23a3a]"
              }`}
            >
              <span className="font-bold">Reading pagination:</span>{" "}
              {readingSlides.length === 1
                ? "1 reading slide"
                : `${readingSlides.length} reading slides`}
              {" · longest chunk "}
              {longestReadingChars} chars{" "}
              {paginationOk
                ? `(within the ${READING_CHUNK_LIMIT}-char projector limit)`
                : `(over the ${READING_CHUNK_LIMIT}-char projector limit — text may shrink or clip)`}
              .
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default DeckMirroringAudit;
